import React from 'react';
import { useAppSelector } from 'hooks';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import Appointments from '~appointments/Appointments';
import GroupAppointments from '~appointments/GroupAppointments';
import Appointment from '~appointments/pages/Appointment';
import AppointmentCancellation from '~appointments/pages/Cancellation';
import RepeatAppointments from '~appointments/RepeatAppointments';
import User from '~care-workers/User';
import Users from '~care-workers/Users';
import Dashboard from '~dashboard/Dashboard';
import WeeklyPlanner from '~weekly-planner/WeeklyPlanner';

import { AuthTags } from '~constants/authTags';
import { getMissingContent } from '~constants/notFoundMap';
import { getTP1Urls } from '~constants/routes-tp1';

import { AccessDenied, PageNotFound, RedirectTo } from '~components';
import { DirectDebitsDashboard } from '~direct-debits/components';
import RoreSessionSearch from '~rore/components/RoreSessionSearch';
import RoreSessionViewer from '~rore/components/RoreSessionViewer';
import {
  SahBudgets,
  SahClaim,
  SahClaims,
  SahCoContributions,
  SahConfig,
  SahDashboard,
  SahSuppliers,
} from '~support-at-home/components';
import { BudgetCopy, BudgetEdit, BudgetNew } from '~support-at-home/components/budgets';
import { BudgetTracker } from '~support-at-home/components/budgets/tracker';

import { selectAuthenticatedUser, selectCompanyDefaults, selectCompanySettings } from '~main/selectors/login';

import Home from '../Home';
import Login from '../Login';
import Logout from '../Logout';
import SessionCheck from '../SessionCheck';
import AppAuthTagRoute from './AppAuthTagRoute';
import AppDynamicRoute from './AppDynamicRoute';
import AppFeatureFlagRoute from './AppFeatureFlagRoute';
import CompanyAccessKeys from './company-access-keys/CompanyAccessKeysContainer';
import PrivateRoute from './PrivateRoute';

const AppRoutes: React.FC = () => {
  const user = useAppSelector(selectAuthenticatedUser);
  const settings = useAppSelector(selectCompanySettings);
  const companyDefaults = useAppSelector(selectCompanyDefaults);
  const tp1urls = getTP1Urls(user, settings, companyDefaults);

  const router = createBrowserRouter([
    {
      path: '/login',
      element: <Login />,
    },
    {
      path: '/logout',
      element: <Logout />,
    },
    {
      path: '/auth-session',
      element: <SessionCheck />,
    },
    {
      element: <PrivateRoute />,
      children: [
        {
          path: '/',
          element: <Home />,
          children: [
            // dashboard
            {
              path: 'home',
              element: <AppFeatureFlagRoute Route={Dashboard} flag={'feature-dashboard'} redirect="/appointments" />,
            },
            // Company Access Tokens
            {
              path: 'company/access-tokens',
              element: (
                <AppFeatureFlagRoute
                  flag={'feature-company-access-tokens'}
                  redirect="/access-denied"
                  Route={() => (
                    <AppAuthTagRoute
                      authTag={AuthTags.COMPANY.API.UPDATE}
                      Route={CompanyAccessKeys}
                      redirect="/access-denied"
                    />
                  )}
                />
              ),
            },

            // appointments
            { path: 'appointments', element: <Appointments key={1} /> },
            { path: 'appointments/new', element: <Appointment key={2} isNew={true} /> },
            { path: 'appointments/repeats', element: <RepeatAppointments key={3} /> },
            { path: 'appointments/repeats/new', element: <Appointment key={4} isNew={true} forceRepeat={true} /> },
            { path: 'appointments/transport/new', element: <Appointment key={5} isNew={true} isTransport={true} /> },

            {
              path: 'appointments/support-coordination/new',
              element: <Appointment key={6} isNew={true} isSupportCoordination={true} />,
            },
            {
              path: 'appointments/case-management/new',
              element: <Appointment key={7} isNew={true} isCaseManagement={true} />,
            },

            { path: 'appointments/:id', element: <AppDynamicRoute Route={Appointment} paramKey={'id'} /> },
            { path: 'appointments/:id/cancel', element: <AppointmentCancellation /> },
            {
              path: 'appointments/groups',
              element: (
                <AppAuthTagRoute
                  authTag={AuthTags.APPOINTMENTS.TAVGROUPS.VIEW}
                  Route={GroupAppointments}
                  isNew={true}
                  isNewGroupParent={true}
                  redirect={tp1urls.APPOINTMENTS.ViewGroupAppointments}
                />
              ),
            },
            {
              path: 'appointments/groups/new',
              element: (
                <AppAuthTagRoute
                  authTag={AuthTags.APPOINTMENTS.TAVGROUPS.VIEW}
                  Route={Appointment}
                  isNew={true}
                  isNewGroupParent={true}
                  redirect={tp1urls.APPOINTMENTS.AddGroupAppointment}
                />
              ),
            },
            {
              path: 'appointments/groups/:group_id/users/new',
              element: (
                <AppAuthTagRoute
                  authTag={AuthTags.APPOINTMENTS.TAVGROUPS.VIEW}
                  Route={Appointment}
                  isNew={true}
                  isNewGroupChild={true}
                  participantType={'user'}
                  redirect={tp1urls.APPOINTMENTS.AddGroupAppointment}
                />
              ),
            },
            {
              path: 'appointments/groups/:group_id/clients/new',
              element: (
                <AppAuthTagRoute
                  authTag={AuthTags.APPOINTMENTS.TAVGROUPS.VIEW}
                  Route={Appointment}
                  isNew={true}
                  isNewGroupChild={true}
                  participantType={'client'}
                  redirect={tp1urls.APPOINTMENTS.AddGroupAppointment}
                />
              ),
            },
            {
              path: 'appointments/groups/repeats/new',
              element: (
                <AppAuthTagRoute
                  authTag={AuthTags.APPOINTMENTS.TAVGROUPS.VIEW}
                  Route={Appointment}
                  isNew={true}
                  forceRepeat={true}
                  isNewGroupParent={true}
                  redirect={tp1urls.APPOINTMENTS.AddGroupAppointment}
                />
              ),
            },
            {
              path: 'appointments/groups/repeats/:group_id/users/new',
              element: (
                <AppAuthTagRoute
                  authTag={AuthTags.APPOINTMENTS.TAVGROUPS.VIEW}
                  Route={Appointment}
                  isNew={true}
                  forceRepeat={true}
                  isNewGroupChild={true}
                  participantType={'user'}
                  redirect={tp1urls.APPOINTMENTS.AddGroupAppointment}
                />
              ),
            },
            {
              path: 'appointments/groups/repeats/:group_id/clients/new',
              element: (
                <AppAuthTagRoute
                  authTag={AuthTags.APPOINTMENTS.TAVGROUPS.VIEW}
                  Route={Appointment}
                  isNew={true}
                  forceRepeat={true}
                  isNewGroupChild={true}
                  participantType={'client'}
                  redirect={tp1urls.APPOINTMENTS.AddGroupAppointment}
                />
              ),
            },
            // users
            {
              path: 'users',
              element: (
                <AppFeatureFlagRoute Route={Users} flag={'feature-users'} redirect={tp1urls.CAREWORKERS.UserAdmin} />
              ),
            },
            {
              path: 'users/:id',
              element: (
                <AppFeatureFlagRoute Route={User} flag={'feature-users'} redirect={tp1urls.CAREWORKERS.UserAdmin} />
              ),
            },

            // Support at Home
            {
              path: 'support-at-home/',
              element: (
                <AppAuthTagRoute
                  Route={SahDashboard}
                  authTag={AuthTags.SUPPORTATHOME.DASHBOARD.VIEW}
                  redirect="/access-denied"
                />
              ),
            },
            {
              path: 'support-at-home/budgets',
              element: (
                <AppAuthTagRoute
                  Route={SahBudgets}
                  authTag={AuthTags.SUPPORTATHOME.BUDGETPLANNER.VIEW}
                  redirect="/access-denied"
                />
              ),
            },
            {
              path: 'support-at-home/budgets/:id',
              element: (
                <AppAuthTagRoute
                  Route={BudgetEdit}
                  authTag={AuthTags.SUPPORTATHOME.BUDGETPLANNER.VIEW}
                  redirect="/access-denied"
                />
              ),
            },
            {
              path: 'support-at-home/budgets/:id/tracker',
              element: (
                <AppAuthTagRoute
                  Route={BudgetTracker}
                  authTag={AuthTags.SUPPORTATHOME.BUDGETPLANNER.VIEW}
                  redirect="/access-denied"
                />
              ),
            },
            {
              path: 'support-at-home/budgets/:id/copy',
              element: (
                <AppAuthTagRoute
                  Route={BudgetCopy}
                  authTag={AuthTags.SUPPORTATHOME.BUDGETPLANNER.VIEW}
                  redirect="/access-denied"
                />
              ),
            },
            {
              path: 'support-at-home/budgets/new',
              element: (
                <AppAuthTagRoute
                  Route={BudgetNew}
                  authTag={AuthTags.SUPPORTATHOME.BUDGETPLANNER.VIEW}
                  redirect="/access-denied"
                />
              ),
            },
            {
              path: 'support-at-home/claims',
              element: (
                <AppAuthTagRoute
                  Route={SahClaims}
                  authTag={AuthTags.SUPPORTATHOME.CLAIMS.VIEW}
                  redirect="/access-denied"
                />
              ),
            },
            {
              path: 'support-at-home/claims/:id',
              element: (
                <AppAuthTagRoute
                  Route={SahClaim}
                  authTag={AuthTags.SUPPORTATHOME.CLAIMS.VIEW}
                  redirect="/access-denied"
                />
              ),
            },
            {
              path: 'support-at-home/suppliers',
              element: (
                <AppAuthTagRoute
                  Route={SahSuppliers}
                  authTag={AuthTags.SUPPORTATHOME.SUPPLIERINVOICES.VIEW}
                  redirect="/access-denied"
                />
              ),
            },
            {
              path: 'support-at-home/co-contributions',
              element: (
                <AppFeatureFlagRoute
                  flag={'feature-sah-co-contributions'}
                  redirect="/access-denied"
                  Route={() => (
                    <AppAuthTagRoute
                      authTag={AuthTags.SUPPORTATHOME.CLAIMS.VIEW} // TODO: update when auth tag added
                      Route={SahCoContributions}
                      redirect="/access-denied"
                    />
                  )}
                />
              ),
            },
            {
              path: 'support-at-home/configs',
              element: (
                <AppAuthTagRoute Route={SahConfig} authTag={AuthTags.SUPPORTATHOME.VIEW} redirect="/access-denied" />
              ),
            },

            // Direct Debits
            {
              path: 'direct-debits',
              element: (
                <AppFeatureFlagRoute
                  flag={'feature-direct-debits'}
                  redirect="/access-denied"
                  Route={() => (
                    <AppAuthTagRoute
                      Route={DirectDebitsDashboard}
                      authTag={AuthTags.DIRECTDEBITS.VIEW}
                      redirect="/access-denied"
                    />
                  )}
                />
              ),
            },

            // RoR.E
            {
              path: 'rore',
              element: <AppFeatureFlagRoute Route={RoreSessionSearch} flag="feature-rore" redirect="/home" />,
            },
            {
              path: 'rore/sessions/:id',
              element: (
                <AppFeatureFlagRoute
                  flag="feature-rore"
                  redirect="/home"
                  Route={() => <AppDynamicRoute Route={RoreSessionViewer} paramKey="id" />}
                />
              ),
            },

            // weekly planner
            {
              path: 'weekly-planner',
              element: (
                <AppAuthTagRoute
                  Route={WeeklyPlanner}
                  authTag={AuthTags.WEEKLYPLANNER.VIEW}
                  redirect={tp1urls.APPOINTMENTS.WeeklyPlanner}
                />
              ),
            },

            { path: 'redirect/*', element: <RedirectTo /> },
            {
              path: 'access-denied',
              element: <AccessDenied />,
            },
            {
              path: '*', // This will catch all unmatched routes
              element: <PageNotFound content={getMissingContent('page')} />,
            },
          ],
        },
      ],
    },
  ]);

  return <RouterProvider router={router} />;
};

export default AppRoutes;
